<section class="patient-results" aria-labelledby="page-title">
  <nav class="breadcrumb flex align-center gap-8 fs-12 mb-15" aria-label="Breadcrumb">
    <a class="breadcrumb__link fw-600" routerLink="/" aria-label="Return to the dashboard"
      >Dashboard</a
    >
    <app-icon
      class="breadcrumb__icon"
      name="chevron_right"
      size="xs"
      color="custom"
      customColor="currentColor"
      role="presentation"
    ></app-icon>
    <a class="breadcrumb__link fw-600" routerLink="/access-controls">Access &amp; Controls</a>
    <app-icon
      class="breadcrumb__icon"
      name="chevron_right"
      size="xs"
      color="custom"
      customColor="currentColor"
      role="presentation"
    ></app-icon>
    <span aria-current="page">Patient Results</span>
  </nav>

  <header class="page-header flex align-start justify-between gap-16 mb-20">
    <div>
      <h1 id="page-title" class="page-title fs-22 fw-700 m-0">Patient Results</h1>
      <p class="page-subtitle m-0 mt-5">
        Patients matching your search. Select one to manage their portal access.
      </p>
    </div>

    <!-- Disabled while a search is outstanding: a second one started mid-flight would race the
         first, and the older response could land last. -->
    <button
      type="button"
      class="secondary-button button--compact"
      [disabled]="isSearching()"
      (click)="openModal($event.currentTarget)"
    >
      <app-icon
        name="manage_search"
        size="xs"
        color="custom"
        customColor="currentColor"
        role="presentation"
      ></app-icon>
      <span>{{ hasCriteria() ? 'Modify Search' : 'Search' }}</span>
    </button>
  </header>

  @if (criteriaChips().length > 0) {
  <div class="criteria flex align-center gap-8 flex-wrap mb-15">
    <span class="criteria__label fs-12 fw-600">Searched for</span>
    @for (chip of criteriaChips(); track chip.label) {
    <span class="criteria__chip fs-12">
      <span class="criteria__chip-label">{{ chip.label }}:</span>
      <span class="criteria__chip-value fw-600">{{ chip.value }}</span>
    </span>
    }
  </div>
  }

  <section class="info-card" aria-labelledby="results-title">
    <div class="info-card__header flex align-center gap-8">
      <!-- `tabindex="-1"` so an applied search can move focus here programmatically without
           adding the heading to the tab order. -->
      <h2 id="results-title" class="info-card__title info-card__title--grow" tabindex="-1">
        Matching Patients
      </h2>
      @if (!isSearching() && hasSearched()) {
      <span class="info-card__meta fs-12">{{ resultSummary() }}</span>
      }
    </div>

    <!-- Outside the state branches below on purpose: an aria-live region has to already be in the
         DOM for a change to be announced, so one created at the same moment its text appears
         announces nothing. -->
    <p class="sr-only" role="status" aria-live="polite">{{ resultSummary() }}</p>

    @if (searchError()) {
    <p class="search-error flex align-center gap-8 fs-12" role="alert">
      <app-icon
        name="info"
        size="xs"
        color="custom"
        customColor="currentColor"
        role="presentation"
      ></app-icon>
      <span>{{ searchError() }}</span>
    </p>
    }

    <!-- The search takes a fixed page with no paging UI, so say so when the server matched more —
         otherwise staff could conclude a patient has no record when their row sat past the
         cut-off. -->
    @if (!isSearching() && isTruncated()) {
    <p class="search-truncated flex align-center gap-8 fs-12" role="status">
      <app-icon
        name="info"
        size="xs"
        color="custom"
        customColor="currentColor"
        role="presentation"
      ></app-icon>
      <span
        >More patients matched than are shown. Narrow the criteria to see the rest before concluding
        a patient has no record.</span
      >
    </p>
    } @if (isSearching()) {
    <div class="results-loading flex align-center gap-10" role="status" aria-live="polite">
      <app-progress-spinner [diameter]="24" />
      <span class="results-loading__text">Searching patients…</span>
    </div>
    } @else if (results().length > 0) {
    <div class="results-table" role="table" aria-label="Patient search results">
      <div class="results-table__header" role="row">
        <span role="columnheader">Patient Name</span>
        <span role="columnheader">DOB</span>
        <span role="columnheader">MRN</span>
        <span role="columnheader">Email</span>
        <span role="columnheader" class="sr-only">Actions</span>
      </div>

      @for (record of results(); track record.patientId) {
      <div class="results-table__row" role="row">
        <span class="results-table__name-cell" role="cell">
          <span class="results-table__avatar" aria-hidden="true">{{ getInitials(record) }}</span>
          <span class="results-table__name fw-600">{{ record.displayName }}</span>
        </span>
        <span class="results-table__cell" role="cell" data-label="DOB"
          >{{ getDateOfBirth(record) }}</span
        >
        <span class="results-table__cell results-table__mrn" role="cell" data-label="MRN"
          >{{ getMedicalRecordNumber(record) }}</span
        >
        <span class="results-table__cell results-table__email" role="cell" data-label="Email"
          >{{ getEmailAddress(record) }}</span
        >
        <span role="cell">
          <!-- An anchor, not a button: this is a navigation, so it has to open in a new tab and
               offer a copyable link like any other. The id travels in the URL (unlike the search
               criteria, which stay in memory) so the detail screen survives a refresh.

               This replaces the disabled placeholder the search-modal branch carried — the detail
               screen it was waiting on now exists. Sizing comes from the shared `button--compact`
               modifier rather than a bespoke rule, so it tracks the other 34px controls. -->
          <a
            class="primary-button button--compact fw-600"
            [routerLink]="['/access-controls/patients', record.patientId]"
            [attr.aria-label]="'Manage access for ' + record.displayName"
          >
            Manage Access
          </a>
        </span>
      </div>
      }
    </div>
    } @else if (searchError()) {
    <!-- Nothing further: the banner above already says the search failed. Falling through to the
         empty states would add "No patient records matched these criteria" underneath it, which is
         the exact wrong conclusion to hand someone checking whether a patient has a record. -->
    } @else if (!hasCriteria()) {
    <div class="empty-state" role="status" aria-live="polite">
      <h3 class="empty-state__title">No search to show</h3>
      <p class="empty-state__message">
        Patient search criteria aren't kept in the address bar, so they don't survive a refresh or a
        shared link. Start a new search to see matching patients.
      </p>
      <button
        type="button"
        class="primary-button button--compact"
        (click)="openModal($event.currentTarget)"
      >
        Search for a Patient
      </button>
    </div>
    } @else {
    <div class="empty-state" role="status" aria-live="polite">
      <h3 class="empty-state__title">No matching patients found</h3>
      <p class="empty-state__message">
        No patient records matched these criteria. Broaden or correct the search and try again.
      </p>
      <button
        type="button"
        class="primary-button button--compact"
        (click)="openModal($event.currentTarget)"
      >
        Modify Search
      </button>
    </div>
    }
  </section>
</section>

@if (isModalOpen()) {
<div class="modal-backdrop" role="presentation" (click)="closeModal()">
  <!-- `stopPropagation` keeps a click inside the dialog from reaching the scrim's close handler.
       `cleared` is deliberately not bound: Clear resets the dialog's own fields, and wiping the
       rows a staff member is still reading would look like the search came back empty. -->
  <app-patient-search-modal
    subtitle="Enter details to find a patient and manage their portal access."
    [initialFilter]="criteria()"
    [minimumCriteria]="minimumCriteria"
    (applied)="onPatientSearch($event)"
    (closed)="closeModal()"
    (click)="$event.stopPropagation()"
  ></app-patient-search-modal>
</div>
}
